import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FocusScope } from '@react-aria/focus';
import {
  Analytics,
  ActionSubjectIdType,
  SourceType,
} from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { useLocation } from '@trello/router';
import { HeaderTestIds } from '@trello/test-ids';
import { escapeReturnUrl } from '@trello/urls';

import { AnalyticsAttributesProvider } from './AnalyticsAttributesProvider';
import { ChevronLeft } from './icons/ChevronLeft';
import { ChevronRight } from './icons/ChevronRight';
import { Logo } from './Logo';
import { MenuIcon } from './MenuIcon';
import styles from './SmallNav.less';
import { SmallNavTabPage } from './SmallNavTabPage';
import { TabId } from './TabId';
import { UiBanner } from './UiBanner';
import { UiNavLink } from './UiNavLink';

const analyticsAttributes = { eventContainer: 'mobileNav' };
const format = forTemplate('logged_out_header');

interface TabLinkProps {
  href?: string;
  index: TabId;
  label?: string;
  tabIndex?: number;
  analyticsLinkName: ActionSubjectIdType;
  analyticsSource: SourceType;
}

const TabLink: React.FunctionComponent<TabLinkProps> = ({
  href,
  label,
  tabIndex,
  analyticsLinkName,
  analyticsSource,
}) => {
  const onClick = useCallback(
    () =>
      Analytics.sendClickedLinkEvent({
        linkName: analyticsLinkName,
        source: analyticsSource,
        attributes: {
          ...analyticsAttributes,
          event: 'clicked',
          eventComponent: 'mobileNavLink',
          eventContainer: 'mobileNav',
          label,
          href,
          isMarketingEvent: true,
        },
      }),
    [href, label, analyticsLinkName, analyticsSource],
  );

  return (
    <a
      className={styles.tabLink}
      href={href}
      onClick={onClick}
      tabIndex={tabIndex}
    >
      {label}
    </a>
  );
};

interface TabButtonProps {
  index: TabId;
  setActiveTab: Dispatch<SetStateAction<number | undefined>>;
  tabIndex?: number;
  title: string;
}

const TabButton: React.FunctionComponent<TabButtonProps> = ({
  index,
  setActiveTab,
  tabIndex,
  title,
}) => {
  const onClick = useCallback(() => {
    const indexedLibrary = new Map();
    indexedLibrary.set(0, 'featuresMenuItem');
    indexedLibrary.set(1, 'solutionsMenuItem');
    indexedLibrary.set(2, 'plansMenuItem');
    indexedLibrary.set(4, 'resourcesMenuItem');

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'menuItem',
      actionSubjectId: indexedLibrary.get(index),
      source: 'anonymousMobileHeaderInlineDialog',
      attributes: {
        event: 'clicked',
        eventComponent: 'mobileNavTab',
        eventContainer: 'mobileNav',
        label: title,
        isMarketingEvent: true,
      },
    });
    setActiveTab(index);
  }, [index, setActiveTab, title]);

  return (
    <Button
      appearance="subtle"
      iconAfter={<ChevronRight />}
      className={styles.tabButton}
      data-test-id={HeaderTestIds.LoggedOutHeaderNarrowTab}
      onClick={onClick}
      tabIndex={tabIndex}
      size="fullwidth"
    >
      {title}
    </Button>
  );
};

interface SmallNavProps {
  hasScrolled: boolean;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}

export const SmallNav: React.FunctionComponent<SmallNavProps> = ({
  hasScrolled,
  onLoginClick,
  onSignupClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<number | undefined>();
  const headerRef = useRef<HTMLDivElement>(null);
  const logInText = format('log-in');
  const signUpText = format('sign-up');
  const goBackText = format('go-back-button-label');

  const { pathname } = useLocation();
  const returnUrl = escapeReturnUrl(pathname);

  const close = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setActiveTab(undefined), 300);
  }, []);

  const open = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'mobileMenuOpenButton',
      source: 'anonymousMobileHeader',
      attributes: {
        ...analyticsAttributes,
        event: 'clicked',
        eventComponent: 'mobileNavMenu',
        label: 'menu open',
        isMarketingEvent: true,
      },
    });
    setIsOpen(true);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [close, isOpen, open]);

  useEffect(() => {
    function escPress(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
      }
    }
    if (isOpen) {
      window.addEventListener('keydown', escPress);
      document.body.classList.add('prevent-scroll');
    }
    return () => {
      window.removeEventListener('keydown', escPress);
      document.body.classList.remove('prevent-scroll');
    };
  }, [close, isOpen]);

  const onClickBack = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'mobileNavBackButton',
      source: 'anonymousMobileHeaderInlineDialog',
      attributes: {
        label: goBackText,
        eventComponent: 'mobileNavBackButton',
        eventContainer: 'mobileNav',
        isMarketingEvent: true,
        event: 'clicked',
      },
    });
    setActiveTab(undefined);
  }, [goBackText, setActiveTab]);

  const onClickSignup = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'getTrelloForFreeLink',
      source: 'anonymousMobileHeaderInlineDialog',
      attributes: {
        event: 'clicked',
        eventComponent: 'mobileNavButton',
        eventContainer: 'mobileNav',
        href: '/signup',
        label: signUpText,
      },
    });
    if (onSignupClick) {
      onSignupClick();
    }
  }, [onSignupClick, signUpText]);

  const onClickLogin = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'logInLink',
      source: 'anonymousMobileHeaderInlineDialog',
      attributes: {
        label: logInText,
        href: '/login',
        event: 'clicked',
        eventComponent: 'mobileNavButton',
        eventContainer: 'mobileNav',
      },
    });
    if (onLoginClick) {
      onLoginClick();
    }
  }, [logInText, onLoginClick]);

  useEffect(() => {
    if (isOpen) {
      Analytics.sendScreenEvent({
        name: 'anonymousMobileHeaderInlineDialog',
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const indexedLibrary = new Map();
    indexedLibrary.set(0, 'anonymousMobileHeaderFeaturesInlineDialog');
    indexedLibrary.set(1, 'anonymousMobileHeaderSolutionsInlineDialog');
    indexedLibrary.set(2, 'anonymousMobileHeaderPlansInlineDialog');
    indexedLibrary.set(4, 'anonymousMobileHeaderResourcesInlineDialog');

    if (indexedLibrary.get(activeTab)) {
      Analytics.sendScreenEvent({
        name: indexedLibrary.get(activeTab),
      });
    }
  }, [activeTab]);

  const smallNavTabIndex = !isOpen || activeTab !== undefined ? -1 : undefined;

  return (
    <FocusScope contain={isOpen} restoreFocus autoFocus>
      <AnalyticsAttributesProvider value={analyticsAttributes}>
        <header
          className={styles.header}
          data-scrolled={!isOpen && hasScrolled}
          data-open={isOpen}
          data-test-id={HeaderTestIds.LoggedOutHeaderNarrow}
        >
          <div className={styles.navBar} ref={headerRef}>
            <div
              className={styles.logoWrapper}
              data-hidden={activeTab !== undefined}
            >
              <Logo navSize="small" tabIndex={isOpen ? -1 : undefined} />
            </div>
            <Button
              appearance="subtle"
              iconBefore={<ChevronLeft />}
              className={styles.backButton}
              data-hidden={activeTab === undefined || !isOpen}
              onClick={onClickBack}
              tabIndex={activeTab === undefined || !isOpen ? -1 : undefined}
            >
              {goBackText}
            </Button>
            <Button
              appearance="subtle"
              aria-expanded={isOpen}
              aria-label={format('navigation-button-label')}
              className={styles.menuButton}
              iconBefore={<MenuIcon isOpen={isOpen} />}
              onClick={toggle}
              data-test-id={HeaderTestIds.LoggedOutHeaderMenuButton}
            />
          </div>
          <div className={styles.pageContainer} data-open={isOpen}>
            {isOpen && activeTab === undefined ? (
              <FocusScope contain restoreFocus autoFocus>
                <div
                  className={styles.page}
                  aria-hidden={!isOpen || activeTab !== undefined}
                  data-offscreen={activeTab !== undefined ? 'left' : undefined}
                  data-test-id={HeaderTestIds.LoggedOutHeaderNarrowTabs}
                >
                  <div className={styles.tabList}>
                    <TabButton
                      index={TabId.Features}
                      setActiveTab={setActiveTab}
                      tabIndex={smallNavTabIndex}
                      title={format('features-tab-label')}
                    />
                    <TabButton
                      index={TabId.Solutions}
                      setActiveTab={setActiveTab}
                      tabIndex={smallNavTabIndex}
                      title={format('solutions-tab-label')}
                    />
                    <TabButton
                      index={TabId.Plans}
                      setActiveTab={setActiveTab}
                      tabIndex={smallNavTabIndex}
                      title={format('plans-tab-label')}
                    />
                    <TabLink
                      href="/pricing"
                      index={TabId.Pricing}
                      label={format('pricing-tab-label')}
                      tabIndex={smallNavTabIndex}
                      analyticsLinkName="pricingLink"
                      analyticsSource="anonymousMobileHeaderInlineDialog"
                    />
                    <TabButton
                      index={TabId.Resources}
                      setActiveTab={setActiveTab}
                      tabIndex={smallNavTabIndex}
                      title={format('resources-tab-label')}
                    />
                  </div>
                  <div className={styles.buttons}>
                    <>
                      <a
                        className={styles.buttonLinkPrimary}
                        href={`/signup?returnUrl=${returnUrl}`}
                        onClick={onClickSignup}
                        tabIndex={smallNavTabIndex}
                      >
                        {signUpText}
                      </a>
                      <a
                        className={styles.buttonLink}
                        href={`/login?returnUrl=${returnUrl}`}
                        onClick={onClickLogin}
                        tabIndex={smallNavTabIndex}
                      >
                        {logInText}
                      </a>
                    </>
                  </div>
                </div>
              </FocusScope>
            ) : null}
            {activeTab === TabId.Features && (
              <SmallNavTabPage
                isActiveTab={activeTab === TabId.Features}
                isOpen={isOpen}
                parentLabel={format('features-tab-label')}
                primaryTitle={format('features-tab-detail-heading')}
                primaryLinks={[
                  <UiNavLink
                    titleHeading={format('features-primary-link-heading-views')}
                    description={format(
                      'features-primary-link-description-views',
                    )}
                    icon="New"
                    url="/views"
                    tabIndex={activeTab === TabId.Features ? undefined : -1}
                    analyticsLinkName="viewsLink"
                    analyticsSource="anonymousMobileHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'features-primary-link-heading-automation',
                    )}
                    description={format(
                      'features-primary-link-description-automation',
                    )}
                    icon="ButlerAutomationHead"
                    url="/butler-automation"
                    tabIndex={activeTab === TabId.Features ? undefined : -1}
                    analyticsLinkName="automationLink"
                    analyticsSource="anonymousMobileHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'features-primary-link-heading-power-ups',
                    )}
                    description={format(
                      'features-primary-link-description-power-ups',
                    )}
                    icon="RocketInclined"
                    url="/power-ups"
                    tabIndex={activeTab === TabId.Features ? undefined : -1}
                    analyticsLinkName="powerupsLink"
                    analyticsSource="anonymousMobileHeaderFeaturesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'features-primary-link-heading-templates',
                    )}
                    description={format(
                      'features-primary-link-description-templates',
                    )}
                    icon="BrowserCode"
                    url="/templates"
                    tabIndex={activeTab === TabId.Features ? undefined : -1}
                    analyticsLinkName="templatesLink"
                    analyticsSource="anonymousMobileHeaderFeaturesInlineDialog"
                  />,
                ]}
                secondaryTitle={format('features-tab-detail-secondary-heading')}
                secondaryDescription={format(
                  'features-tab-detail-secondary-description',
                )}
                secondaryButtonText={format(
                  'features-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/tour"
                analyticsLinkName="takeATourOfTrelloLink"
                analyticsSource="anonymousMobileHeaderFeaturesInlineDialog"
              />
            )}
            {activeTab === TabId.Solutions && (
              <SmallNavTabPage
                isActiveTab={activeTab === TabId.Solutions}
                isOpen={isOpen}
                parentLabel={format('solutions-tab-label')}
                primaryTitle={format('solutions-tab-detail-heading')}
                primaryLinks={[
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-marketing',
                    )}
                    description={format(
                      'solutions-primary-link-description-marketing',
                    )}
                    icon="Megaphone"
                    url="/teams/marketing"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="marketingTeamsLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-product',
                    )}
                    description={format(
                      'solutions-primary-link-description-product',
                    )}
                    icon="Box"
                    url="/teams/product"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="productManagementLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-engineering',
                    )}
                    description={format(
                      'solutions-primary-link-description-engineering',
                    )}
                    icon="BrowserCode"
                    url="/teams/engineering"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="engineeringTeamsLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-design',
                    )}
                    description={format(
                      'solutions-primary-link-description-design',
                    )}
                    icon="Pencil"
                    url="/teams/design"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="designTeamLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-startups',
                    )}
                    description={format(
                      'solutions-primary-link-description-startups',
                    )}
                    icon="Lego"
                    url="/teams/startups"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="startupsLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'solutions-primary-link-heading-remote',
                    )}
                    description={format(
                      'solutions-primary-link-description-remote',
                    )}
                    icon="GlobeNetworkLocation"
                    url="/teams/remote-team-management"
                    tabIndex={activeTab === TabId.Solutions ? undefined : -1}
                    analyticsLinkName="remoteTeamsLink"
                    analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
                  />,
                ]}
                primaryLinkText={format(
                  'solutions-tab-detail-primary-link-text',
                )}
                primaryLinkUrl="/teams"
                secondaryTitle={format(
                  'solutions-tab-detail-secondary-heading',
                )}
                secondaryDescription={format(
                  'solutions-tab-detail-secondary-description',
                )}
                secondaryButtonText={format(
                  'solutions-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/use-cases"
                analyticsLinkName="seeAllUseCasesLink"
                analyticsSource="anonymousMobileHeaderSolutionsInlineDialog"
              />
            )}
            {activeTab === TabId.Plans && (
              <SmallNavTabPage
                isActiveTab={activeTab === TabId.Plans}
                isOpen={isOpen}
                parentLabel={format('plans-tab-label')}
                primaryBanner={
                  <UiBanner
                    type="uiBanner"
                    titleHeading={format('plans-primary-banner-header-free')}
                    description={format(
                      'plans-primary-banner-description-free',
                    )}
                    buttonText={format('plans-primary-banner-button-text-free')}
                    buttonUrl="/tour"
                    tabIndex={activeTab === TabId.Plans ? undefined : -1}
                    analyticsLinkName="takeATourOfTrelloLink"
                    analyticsSource="anonymousMobileHeaderPlansInlineDialog"
                  />
                }
                primaryLinks={[
                  <UiNavLink
                    titleHeading={format('plans-primary-link-header-standard')}
                    description={format(
                      'plans-primary-link-description-standard',
                    )}
                    icon="Lightbulb"
                    iconColor="Teal"
                    url="/standard"
                    tabIndex={activeTab === TabId.Plans ? undefined : -1}
                    analyticsLinkName="standardLink"
                    analyticsSource="anonymousMobileHeaderPlansInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format('plans-primary-link-header-premium')}
                    description={format(
                      'plans-primary-link-description-premium',
                    )}
                    icon="Star"
                    iconColor="Magenta"
                    url="/premium"
                    tabIndex={activeTab === TabId.Plans ? undefined : -1}
                    analyticsLinkName="premiumLink"
                    analyticsSource="anonymousMobileHeaderPlansInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'plans-primary-link-header-enterprise',
                    )}
                    description={format(
                      'plans-primary-link-description-enterprise',
                    )}
                    icon="Building"
                    iconColor="Blue"
                    url="/enterprise"
                    tabIndex={activeTab === TabId.Plans ? undefined : -1}
                    analyticsLinkName="enterpriseLink"
                    analyticsSource="anonymousMobileHeaderPlansInlineDialog"
                  />,
                ]}
                secondaryTitle={format(
                  'plans-tab-detail-secondary-heading',
                ).replace(/&amp;/g, '&')}
                secondaryDescription={format(
                  'plans-tab-detail-secondary-description',
                )}
                secondaryButtonText={format(
                  'plans-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="/pricing"
                analyticsLinkName="viewTrelloPricingLink"
                analyticsSource="anonymousMobileHeaderPlansInlineDialog"
              />
            )}
            {activeTab === TabId.Resources && (
              <SmallNavTabPage
                isActiveTab={activeTab === TabId.Resources}
                isOpen={isOpen}
                parentLabel={format('resources-tab-label')}
                primaryLinks={[
                  <UiNavLink
                    titleHeading={format(
                      'resources-primary-link-header-trello-guide',
                    )}
                    description={format(
                      'resources-primary-link-description-trello-guide',
                    )}
                    url="/guide"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="trelloGuideLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'resources-primary-link-header-remote-guide',
                    )}
                    description={format(
                      'resources-primary-link-description-remote-guide',
                    )}
                    url="/guide/remote-work"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="remoteWorkGuideLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format('resources-primary-link-header-blog')}
                    description={format(
                      'resources-primary-link-description-blog',
                    )}
                    url="https://blog.trello.com"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="trelloBlogLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'resources-primary-link-header-webinars',
                    )}
                    description={format(
                      'resources-primary-link-description-webinars',
                    )}
                    url="/webinars"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="webinarsLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format(
                      'resources-primary-link-header-developers',
                    )}
                    description={format(
                      'resources-primary-link-description-developers',
                    )}
                    url="https://developer.atlassian.com/cloud/trello"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="developersLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                  <UiNavLink
                    titleHeading={format('resources-primary-link-header-help')}
                    description={format(
                      'resources-primary-link-description-help',
                    )}
                    url="https://help.trello.com"
                    tabIndex={activeTab === TabId.Resources ? undefined : -1}
                    analyticsLinkName="helpResourcesLink"
                    analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
                  />,
                ]}
                primaryTitle={format('resources-tab-detail-heading').replace(
                  /&amp;/g,
                  '&',
                )}
                secondaryTitle={format(
                  'resources-tab-detail-secondary-heading',
                )}
                secondaryDescription={format(
                  'resources-tab-detail-secondary-description',
                )}
                secondaryButtonText={format(
                  'resources-tab-detail-secondary-button-text',
                )}
                secondaryButtonUrl="https://blog.trello.com"
                analyticsLinkName="checkOutTheTrelloBlogLink"
                analyticsSource="anonymousMobileHeaderResourcesInlineDialog"
              />
            )}
          </div>
        </header>
      </AnalyticsAttributesProvider>
    </FocusScope>
  );
};
